import { Component } from '@theme/component';

export class DaBannerSlider extends Component {
  requiredRefs = ['track'];

  constructor() {
    super();
    this.activeIndex = 0;
    this.autoplayTimer = null;
    this.startX = 0;
    this.endX = 0;
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Cache slide elements
    this.slides = Array.from(this.refs.track.querySelectorAll('.da-banner-slider__slide'));
    if (this.slides.length === 0) return;

    // Config parameters
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
    this.animationType = this.dataset.animationType || 'slide';

    // Set up listeners
    this.initEventListeners();
    
    // Initial display
    this.goToSlide(0);
    this.startAutoplay();
  }

  initEventListeners() {
    // Navigation arrows
    if (this.refs.prevBtn) {
      this.refs.prevBtn.addEventListener('click', () => {
        this.prevSlide();
      });
    }

    if (this.refs.nextBtn) {
      this.refs.nextBtn.addEventListener('click', () => {
        this.nextSlide();
      });
    }

    // Pagination dots
    if (this.refs.dotsContainer) {
      this.refs.dotsContainer.addEventListener('click', (event) => {
        const dot = event.target.closest('.da-banner-slider__dot');
        if (dot) {
          const index = parseInt(dot.getAttribute('data-dot-index'), 10);
          this.goToSlide(index);
        }
      });
    }

    // Touch events for mobile swipes
    this.addEventListener('touchstart', (event) => {
      this.startX = event.touches[0].clientX;
    }, { passive: true });

    this.addEventListener('touchend', (event) => {
      this.endX = event.changedTouches[0].clientX;
      this.handleSwipe();
    }, { passive: true });

    // Autoplay hover control
    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());

    // Theme Editor select events
    this.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('.da-banner-slider__slide');
      if (slide && this.contains(slide)) {
        const index = parseInt(slide.getAttribute('data-index'), 10);
        if (!isNaN(index)) {
          this.goToSlide(index);
          this.pauseAutoplay();
        }
      }
    });

    this.addEventListener('shopify:block:deselect', () => {
      this.startAutoplay();
    });
  }

  handleSwipe() {
    const swipeThreshold = 55; // pixels
    const diff = this.startX - this.endX;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swiped left, show next slide
        this.nextSlide();
      } else {
        // Swiped right, show prev slide
        this.prevSlide();
      }
    }
  }

  goToSlide(index) {
    if (this.slides.length === 0) return;

    // Remove active state from current items
    this.slides[this.activeIndex].classList.remove('is-active');
    
    // Handle wrap around
    if (index >= this.slides.length) {
      this.activeIndex = 0;
    } else if (index < 0) {
      this.activeIndex = this.slides.length - 1;
    } else {
      this.activeIndex = index;
    }

    // Set new active slide
    this.slides[this.activeIndex].classList.add('is-active');

    // Update Slide Animation
    if (this.animationType === 'slide') {
      this.refs.track.style.transform = `translateX(${this.activeIndex * -100}%)`;
    }

    // Update Dots
    if (this.refs.dotsContainer) {
      const dots = Array.from(this.refs.dotsContainer.querySelectorAll('.da-banner-slider__dot'));
      dots.forEach((dot, dotIndex) => {
        if (dotIndex === this.activeIndex) {
          dot.classList.add('is-active');
          dot.setAttribute('aria-selected', 'true');
        } else {
          dot.classList.remove('is-active');
          dot.setAttribute('aria-selected', 'false');
        }
      });
    }

    // Reset autoplay timer on manual transition
    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  nextSlide() {
    this.goToSlide(this.activeIndex + 1);
  }

  prevSlide() {
    this.goToSlide(this.activeIndex - 1);
  }

  startAutoplay() {
    if (!this.autoplay || this.slides.length <= 1) return;
    this.pauseAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }

  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.pauseAutoplay();
  }
}

if (!customElements.get('da-banner-slider')) {
  customElements.define('da-banner-slider', DaBannerSlider);
}
